import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { analyticsApi, ApiRequestError, type TrainingNeedsDto } from "../../api/api";
import {
  AnalyticsFilterBar,
  lineTitle,
  type AnalyticsFiltersState,
} from "../../components/ui/AnalyticsFilterBar";
import { PageHeader } from "../../components/ui/PageHeader";

export function TrainingNeedsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AnalyticsFiltersState>({
    year: new Date().getFullYear(),
  });
  const [weighted, setWeighted] = useState(false);
  const [data, setData] = useState<TrainingNeedsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void analyticsApi
      .trainingNeeds({ ...filters, weighted })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, weighted, t]);

  const maxGap = data?.needs[0]?.cumulativeGap || 1;

  return (
    <div className="sm-page">
      <PageHeader title={t("training_needs")} />
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      <label className="sm-checkbox-row" style={{ marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={weighted}
          onChange={(e) => setWeighted(e.target.checked)}
        />
        {t("weight_by_ci")}
      </label>
      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? (
        <p className="sm-muted">{t("loading")}</p>
      ) : (
        <div className="sm-card sm-card-pad">
          <div className="sm-table-wrap">
            <table className="sm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Skill / BPML</th>
                  <th>{t("complexity_index")}</th>
                  <th>{t("cumulative_gap")}</th>
                  <th># Experts</th>
                </tr>
              </thead>
              <tbody>
                {(data?.needs ?? []).map((n, i) => {
                  const { title, subtitle } = lineTitle(n.line);
                  return (
                    <tr key={n.line.id}>
                      <td style={{ textAlign: "center" }}>{i + 1}</td>
                      <td>
                        <div>{title}</div>
                        <div className="sm-muted" style={{ fontSize: 11 }}>
                          {subtitle}
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>{n.line.complexityIndex}</td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div
                            style={{
                              background: "#FECACA",
                              height: 8,
                              borderRadius: 4,
                              width: `${Math.min(100, (n.cumulativeGap / maxGap) * 80)}px`,
                            }}
                          />
                          <strong style={{ color: "#DC2626" }}>{n.cumulativeGap}</strong>
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>{n.expertCount}</td>
                    </tr>
                  );
                })}
                {data && data.needs.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", color: "#059669" }}>
                      {t("training_none")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
